import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Grid,
  Alert,
  AlertTitle,
  Card,
  CardMedia,
  CardContent,
  CardHeader,
  Divider,
  Link,
  Stack,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  IconButton,
  Tooltip,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import SensorsOutlinedIcon from "@mui/icons-material/SensorsOutlined";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { API_BASE } from "../lib/api";
import PageIntro from "../components/PageIntro";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import LazySyncedMetricCharts from "../components/LazySyncedMetricCharts";
import useMyFields from "../hooks/useMyFields";
import { getFieldOwnerLabel } from "../lib/fieldAccess";
import { authFieldSx } from "./authFieldStyles";

const CROP_STAGES = [
  "Germination",
  "Seedling",
  "Tillering",
  "Jointing",
  "Stem elongation",
  "Heading",
  "Flowering",
  "Grain filling",
  "Maturity",
];
const CROP_STAGE_COLORS = {
  Germination: "#2E7D32",
  Seedling: "#43A047",
  Tillering: "#66BB6A",
  Jointing: "#9CCC65",
  "Stem elongation": "#CDDC39",
  Heading: "#FBC02D",
  Flowering: "#F9A825",
  "Grain filling": "#EF6C00",
  Maturity: "#8D6E63",
};
const DAILY_DATA_TEMPLATE_URL = "/templates/SEEDS_field_measurements_template.xlsx";
const MAX_DAILY_IMPORT_BYTES = 2 * 1024 * 1024;
const capitalizeFirst = (value) => {
  const clean = String(value || "").trim();
  return clean ? `${clean.charAt(0).toUpperCase()}${clean.slice(1)}` : "";
};

// ----------------------------------------------------------------------
// Field Data
/* Legacy static field catalog retained only for reference. Field choices now
come from the authenticated user's /fields/mine/ endpoint.
const allFieldsData = {
  Jordan: [
    {
      id: "maru",
      name: "Maru station",
      city: "Irbid",
      west: 35.901017,
      south: 32.60608,
      variety: "N/A",
      acres: "N/A",
    },
    {
      id: "mushaqar",
      name: "Mushaqar Center",
      city: "Madaba",
      west: 35.801444,
      south: 31.775077,
      variety: "N/A",
      acres: "N/A",
    },
    {
      id: "rabba",
      name: "Rabba Center",
      city: "Karak",
      west: 35.742623,
      south: 31.276369,
      variety: "N/A",
      acres: "N/A",
    },
  ],
  Egypt: [
    {
      id: "sakha",
      name: "Sakha Agricultural Research Station",
      city: "Sakha",
      west: 30.9458,
      south: 31.1117,
      variety: "N/A",
      acres: "N/A",
    },
    {
      id: "gemmeiza",
      name: "El Gemmiza Agricultural Research Station",
      city: "El Gemmiza",
      west: 31.028,
      south: 30.867,
      variety: "N/A",
      acres: "N/A",
    },
    {
      id: "nubaria",
      name: "Nubaria Agricultural Research Station",
      city: "Nubaria",
      west: 30.66713,
      south: 30.6973,
      variety: "N/A",
      acres: "N/A",
    },
    {
      id: "side",
      name: "Side Agricultural Research Station",
      city: "Side",
      west: 31.097,
      south: 29.076,
      variety: "N/A",
      acres: "N/A",
    },
  ],
  Morocco: [
    {
      id: "marchouch",
      name: "Marchouch",
      city: "Marchouch",
      west: null,
      south: null,
      variety: "N/A",
      acres: "N/A",
    },
    {
      id: "sidi_alaydi",
      name: "Sidi Alaydi",
      city: "Sidi Alaydi",
      west: null,
      south: null,
      variety: "N/A",
      acres: "N/A",
    },
  ],
  Tunisia: [
    {
      id: "inrat",
      name: "INRAT experimental station",
      city: "Tunis",
      west: 10.1925,
      south: 36.8447,
      variety: "N/A",
      acres: "12.5",
    },
    {
      id: "bourabia",
      name: "Bourabia experimental station",
      city: "Bourabia",
      west: 10.1297,
      south: 36.5981,
      variety: "N/A",
      acres: "8.2",
    },
    {
      id: "chebika",
      name: "Chebika (Kairouan)",
      city: "Kairouan",
      west: 9.9358,
      south: 35.6211,
      variety: "N/A",
      acres: "N/A",
    },
    {
      id: "boussalem",
      name: "Boussalem experimental station",
      city: "Boussalem",
      west: 8.9529,
      south: 36.534,
      variety: "N/A",
      acres: "N/A",
    },
    {
      id: "mornag",
      name: "Mornag experimental station",
      city: "Mornag",
      west: null,
      south: null,
      variety: "N/A",
      acres: "N/A",
    },
  ],
};
*/

// const mockBestPractices = [
//   "Use pesticides to cope with the leaf disease.",
//   "Ensure proper irrigation based on humidity levels.",
//   "Check soil pH weekly during this season.",
// ];

// ----------------------------------------------------------------------
function AreaGradient({ color, id }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.5} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}
AreaGradient.propTypes = {
  color: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
};

function formatDate(date) {
  return date.toLocaleString("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

function formatDisplayDate(value) {
  const parsed = value instanceof Date ? value : new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function DailyDataDay({ dataStages = {}, day, sx, ...other }) {
  const stage = dataStages[formatDate(day)];
  const markerColor = CROP_STAGE_COLORS[stage];
  return (
    <PickersDay
      {...other}
      day={day}
      sx={[
        sx,
        markerColor && {
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 2,
            left: "50%",
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: markerColor,
            boxShadow: "0 0 0 1px #fff",
            transform: "translateX(-50%)",
          },
        },
      ]}
    />
  );
}

DailyDataDay.propTypes = {
  dataStages: PropTypes.objectOf(PropTypes.string),
  day: PropTypes.instanceOf(Date).isRequired,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
};

function DataRow({ label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 0.35,
        gap: 1.5,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.9rem",
          color: "text.secondary",
          lineHeight: 1.3,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: "0.9rem",
          fontWeight: 600,
          color: "text.primary",
          textAlign: "right",
          lineHeight: 1.3,
          whiteSpace: "nowrap",
        }}
      >
        {value ?? "-"}
      </Typography>
    </Box>
  );
}

function FieldInfoRow({ label, value }) {
  const displayValue =
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "")
      ? "N/A"
      : value;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(110px, 135px) minmax(0, 1fr)",
        gap: 1.75,
        alignItems: "center",
        py: 0.65,
      }}
    >
      <Typography sx={{ fontSize: "0.98rem", fontWeight: 600 }}>
        {label}:
      </Typography>
      <Typography
        sx={{
          minWidth: 0,
          color: "#9a6a00",
          fontSize: "1.02rem",
          fontWeight: 600,
          overflowWrap: "anywhere",
        }}
      >
        {displayValue}
      </Typography>
    </Box>
  );
}

const professionalCardSx = {
  height: "100%",
  bgcolor: "#fff",
  border: "1px solid",
  borderColor: "#e5e7eb",
  borderRadius: 2.5,
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
};

const dailyDataSectionCardSx = {
  borderColor: "#e2e8f0",
  borderRadius: 2.5,
  boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)",
  bgcolor: "#fff",
};

const dailyDataSectionIconSx = {
  width: 34,
  height: 34,
  borderRadius: 1.5,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: "#f1f5f9",
  color: "#475569",
};

const yellowActionButtonSx = {
  minWidth: 210,
  textTransform: "none",
  fontWeight: 700,
  "&&, &&.MuiButton-contained, &&.MuiButton-containedPrimary": {
    backgroundColor: "#f7b900",
    backgroundImage: "none",
    border: "1px solid #f7b900",
    color: "#1f1f1f",
    boxShadow: "none",
  },
  "&&:hover, &&.MuiButton-contained:hover, &&.MuiButton-containedPrimary:hover": {
    backgroundColor: "#d99f00",
    backgroundImage: "none",
    borderColor: "#d99f00",
    boxShadow: "none",
  },
  "&&.Mui-disabled": {
    backgroundColor: "#f7d77c",
    borderColor: "#f7d77c",
    color: "rgba(31, 31, 31, 0.55)",
  },
};

const yellowSelectFieldSx = {
  ...authFieldSx,
  "& .MuiInputBase-root": {
    height: 38,
    backgroundColor: "#fff",
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "#f7b900" },
    "&:hover fieldset": { borderColor: "#dfa700" },
    "&.Mui-focused fieldset": {
      borderColor: "#d99f00",
      borderWidth: 2,
    },
  },
};

const fieldSelectMenuProps = {
  PaperProps: {
    sx: {
      bgcolor: "#fff",
      border: "1px solid #e8e8e8",
      boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
      "& .MuiMenuItem-root": {
        backgroundColor: "#fff !important",
        outline: "none !important",
        boxShadow: "none !important",
        border: "none",
        "&:hover, &:focus, &:focus-visible, &.Mui-focusVisible": {
          backgroundColor: "#fffaf0 !important",
          outline: "none !important",
          boxShadow: "none !important",
        },
        "&.Mui-selected": {
          backgroundColor: "#fff7d6 !important",
        },
        "&.Mui-selected:hover, &.Mui-selected:focus, &.Mui-selected:focus-visible, &.Mui-selected.Mui-focusVisible": {
          backgroundColor: "#ffefb0 !important",
          outline: "none !important",
          boxShadow: "none !important",
        },
      },
    },
  },
};

const disabledCoordinateFieldSx = {
  "& .MuiInputBase-root": {
    height: 34,
    backgroundColor: "#f3f4f6",
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#f3f4f6",
    color: "#6b7280",
    "& fieldset": { borderColor: "#d1d5db" },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "#6b7280",
  },
  "& .MuiFormHelperText-root": {
    mx: 0,
    mt: 0.5,
    fontSize: "0.65rem",
    whiteSpace: "nowrap",
  },
};

const metricHeadingSx = {
  color: "text.primary",
  fontWeight: 700,
  letterSpacing: 0.7,
};

// ----------------------------------------------------------------------
export default function PlantPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // -----------------------------
  // Main state
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedFieldId, setSelectedFieldId] = useState("");
  const {
    fieldsByCountry,
    countries: fieldCountries,
    owners: fieldOwners,
    selectedOwner,
    selectedOwnerId,
    setSelectedOwnerId,
    canSelectOwner,
    isViewingOwnFields,
    loading: fieldsLoading,
    error: fieldsError,
  } = useMyFields();
  const [calendarDate, setCalendarDate] = useState(new Date());

  const [currentTab, setCurrentTab] = useState(0);

  // NDVI
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState(null);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState(null);

  // Alerts
  // const [alerts] = useState([
  //   {
  //     severity: "warning",
  //     title: "Drop in recommended pH",
  //     date: "12/7/2024",
  //     message: "pH levels dropped to 5.8.",
  //   },
  //   {
  //     severity: "error",
  //     title: "Leaf disease",
  //     date: "5/6/2024",
  //     message: "Signs of mildew detected.",
  //   },
  // ]);

  // Daily data
  const [dailyData, setDailyData] = useState([]);
  const [dailyDataLoading, setDailyDataLoading] = useState(false);
  const [dailyDataError, setDailyDataError] = useState("");

  // History data
  const [dailyHistory, setDailyHistory] = useState([]);
  const [dailyHistoryLoading, setDailyHistoryLoading] = useState(false);
  const [dailyHistoryError, setDailyHistoryError] = useState("");
  const [cropCycles, setCropCycles] = useState([]);
  const [cropCyclesLoading, setCropCyclesLoading] = useState(false);

  // Daily add/edit dialog
  const [dailyDialogOpen, setDailyDialogOpen] = useState(false);
  const [dailyFormLoading, setDailyFormLoading] = useState(false);
  const [dailyFormError, setDailyFormError] = useState("");
  const [dailyDataToDelete, setDailyDataToDelete] = useState(null);
  const [dailyDeleteLoading, setDailyDeleteLoading] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importPolicy, setImportPolicy] = useState("skip_existing");
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [dailyForm, setDailyForm] = useState({
    crop: "",
    crop_stage: "",
    soil_moisture: "",
    soil_temperature: "",
    electrical_conductivity: "",
    soil_ph: "",
    etc: "",
    rainfall: "",
    min_air_temp: "",
    air_temperature: "",
    max_air_temp: "",
    relative_humidity: "",
  });

  const [recommenderLoading, setRecommenderLoading] = useState(false);
  const [recommenderError, setRecommenderError] = useState("");
  const [recommenderResult, setRecommenderResult] = useState(null);

  // -----------------------------
  // Memo
  const availableFields = useMemo(
    () => fieldsByCountry[selectedCountry] || [],
    [fieldsByCountry, selectedCountry]
  );

  const currentFieldInfo = useMemo(
    () => availableFields.find((f) => f.id === selectedFieldId) || {},
    [selectedFieldId, availableFields]
  );

  const selectedDateCycle = useMemo(() => {
    const selectedDate = formatDate(calendarDate);
    if (!selectedDate) return null;
    return cropCycles.find(
      (cycle) =>
        cycle.start_date <= selectedDate &&
        (!cycle.end_date || selectedDate <= cycle.end_date)
    ) || null;
  }, [calendarDate, cropCycles]);

  const selectedCycleIsCompleted = selectedDateCycle?.status === "completed";

  const activeCycle = useMemo(
    () => cropCycles.find((cycle) => cycle.status === "active") || null,
    [cropCycles]
  );

  const createExtentFromPoint = (lon, lat, delta) => ({
    west: lon - delta,
    south: lat - delta,
    east: lon + delta,
    north: lat + delta,
  });

  const coords = useMemo(
    () =>
      typeof currentFieldInfo.west === "number" &&
      typeof currentFieldInfo.south === "number"
        ? createExtentFromPoint(currentFieldInfo.west, currentFieldInfo.south, 0.012)
        : { west: null, south: null, east: null, north: null },
    [currentFieldInfo]
  );

  const outlineCoords = useMemo(
    () =>
      typeof currentFieldInfo.west === "number" &&
      typeof currentFieldInfo.south === "number"
        ? createExtentFromPoint(currentFieldInfo.west, currentFieldInfo.south, 0.001)
        : null,
    [currentFieldInfo]
  );

  const isCoordsValid = useMemo(() => {
    const { west, south, east, north } = coords;
    return (
      [west, south, east, north].every(
        (v) => typeof v === "number" && !Number.isNaN(v)
      ) &&
      east > west &&
      north > south
    );
  }, [coords]);

  const weatherHistoryData = useMemo(() => {
    return dailyHistory
      .filter((item) => item.date)
      .map((item) => ({
        date: new Date(item.date),
        air_temperature: item.air_temperature,
        rainfall: item.rainfall,
        relative_humidity: item.relative_humidity,
        soil_moisture: item.soil_moisture,
        soil_temperature: item.soil_temperature,
        electrical_conductivity: item.electrical_conductivity,
      }));
  }, [dailyHistory]);

  const soilHistoryData = useMemo(() => {
    return dailyHistory
      .filter((item) => item.date)
      .map((item) => ({
        date: new Date(item.date),
        soil_moisture: item.soil_moisture,
        soil_temperature: item.soil_temperature,
        electrical_conductivity: item.electrical_conductivity,
        air_temperature: item.air_temperature,
        rainfall: item.rainfall,
        relative_humidity: item.relative_humidity,
      }));
  }, [dailyHistory]);

  const dailyDataStages = useMemo(
    () => Object.fromEntries(
      dailyHistory
        .filter((item) => item.date && item.crop_stage)
        .map((item) => [item.date, item.crop_stage])
    ),
    [dailyHistory]
  );

  // -----------------------------
  // Effects
  useEffect(() => {
    const c = searchParams.get("country");
    const fid = searchParams.get("field");

    if (c) setSelectedCountry(c);
    if (fid) setSelectedFieldId(fid);
  }, [searchParams]);

  useEffect(() => {
    if (!fieldCountries.length) {
      setSelectedCountry("");
      setSelectedFieldId("");
      return;
    }

    const country = fieldCountries.includes(selectedCountry)
      ? selectedCountry
      : fieldCountries[0];
    const available = fieldsByCountry[country] || [];
    const selected = available.find(
      (field) => field.id === String(selectedFieldId) || field.external_id === selectedFieldId
    );

    if (country !== selectedCountry) setSelectedCountry(country);
    if (!selected) setSelectedFieldId(available[0]?.id || "");
    else if (selected.id !== selectedFieldId) setSelectedFieldId(selected.id);
  }, [fieldCountries, fieldsByCountry, selectedCountry, selectedFieldId]);

  // Reset section state on field change
  useEffect(() => {
    setImages(null);
    setCurrentTab(0);
    setError("");
    setDailyDialogOpen(false);
    setImportDialogOpen(false);
    setDailyDataToDelete(null);
  }, [selectedCountry, selectedFieldId]);

  // -----------------------------
  // Backend fetches
  const fetchDailyDataFromBackend = async () => {
    try {
      setDailyDataLoading(true);
      setDailyDataError("");

      const apiDate = formatDate(calendarDate);
      if (!selectedFieldId || !apiDate) {
        setDailyData([]);
        return;
      }

      const response = await api.get("/daily-data/", {
        params: { field: selectedFieldId, date: apiDate },
      });
      const result = response.data;
      setDailyData(result);
      if (Array.isArray(result) && result.length > 0 && result[0].recommender_result) {
          setRecommenderResult({
            result: result[0].recommender_result,
          });
        } else {
          setRecommenderResult(null);
        }
    } catch (err) {
      console.error("Error fetching backend daily data:", err);
      setDailyData([]);
      setDailyDataError("Could not load daily data.");
    } finally {
      setDailyDataLoading(false);
    }
  };

  const fetchDailyHistoryFromBackend = async () => {
    try {
      setDailyHistoryLoading(true);
      setDailyHistoryError("");

      if (!selectedFieldId) {
        setDailyHistory([]);
        return;
      }

      const response = await api.get("/daily-data/history/", {
        params: { field: selectedFieldId },
      });
      const result = response.data;
      setDailyHistory(result);
    } catch (err) {
      console.error("Error fetching daily history:", err);
      setDailyHistory([]);
      setDailyHistoryError("Could not load historical data.");
    } finally {
      setDailyHistoryLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    if (!selectedFieldId) {
      setCropCycles([]);
      return undefined;
    }

    setCropCyclesLoading(true);
    api.get(`/fields/mine/${selectedFieldId}/crop-cycles/`)
      .then((response) => {
        if (active) setCropCycles(Array.isArray(response.data) ? response.data : []);
      })
      .catch(() => {
        if (active) setCropCycles([]);
      })
      .finally(() => {
        if (active) setCropCyclesLoading(false);
      });

    return () => {
      active = false;
    };
  }, [selectedFieldId]);

  useEffect(() => {
    fetchDailyDataFromBackend();
  }, [selectedCountry, selectedFieldId, calendarDate, currentFieldInfo]);

  useEffect(() => {
    fetchDailyHistoryFromBackend();
  }, [selectedCountry, selectedFieldId, currentFieldInfo]);

  // -----------------------------
  // Handlers
  const handleCountryChange = (e) => {
    const newCountry = e.target.value;
    setSelectedCountry(newCountry);
    setSelectedFieldId(fieldsByCountry[newCountry]?.[0]?.id || "");
  };

  const handleFieldChange = (e) => {
    setSelectedFieldId(e.target.value);
  };

  const handleSubmitNDVI = async () => {
    setLoading(true);
    setError("");
    setImages(null);

    const payload = {
      area_name: currentFieldInfo.name,
      ...coords,
      outline_extent: outlineCoords,
      date: formatDate(calendarDate),
    };

    try {
      const res = await api.post("/generate-ndvi/", payload);
      if (res.data.status === "success") {
        setImages(res.data);
      } else {
        setError("⚠️ No data for this area.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "❌ Error downloading data."
      );
    }
    setLoading(false);
  };

  const openImageModal = (src) => {
    setModalImageSrc(src);
    setIsModalOpen(true);
  };

  const closeImageModal = () => {
    setIsModalOpen(false);
    setModalImageSrc(null);
  };

  const resetDailyForm = () => {
    setDailyForm({
      crop: selectedDateCycle?.crop_display || selectedDateCycle?.crop || "",
      crop_stage: "",
      soil_moisture: "",
      soil_temperature: "",
      electrical_conductivity: "",
      soil_ph: "",
      etc: "",
      rainfall: "",
      min_air_temp: "",
      air_temperature: "",
      max_air_temp: "",
      relative_humidity: "",
    });
  };

  const handleOpenDailyDialog = () => {
    if (!isViewingOwnFields) return;
    setDailyFormError("");

    if (!selectedDateCycle || selectedCycleIsCompleted) return;

    if (dailyData.length > 0) {
      const item = dailyData[0];
      setDailyForm({
        crop: selectedDateCycle?.crop_display || item.crop || "",
        crop_stage: item.crop_stage ?? "",
        soil_moisture: item.soil_moisture ?? "",
        soil_temperature: item.soil_temperature ?? "",
        electrical_conductivity: item.electrical_conductivity ?? "",
        soil_ph: item.soil_ph ?? "",
        etc: item.etc ?? "",
        rainfall: item.rainfall ?? "",
        min_air_temp: item.min_air_temp ?? "",
        air_temperature: item.air_temperature ?? "",
        max_air_temp: item.max_air_temp ?? "",
        relative_humidity: item.relative_humidity ?? "",
      });
    } else {
      const previousDate = new Date(calendarDate);
      previousDate.setDate(previousDate.getDate() - 1);
      const previousDayData = dailyHistory.find(
        (item) =>
          item.date === formatDate(previousDate) &&
          String(item.crop_cycle) === String(selectedDateCycle?.id)
      );

      if (previousDayData) {
        setDailyForm({
          crop: selectedDateCycle?.crop_display || previousDayData.crop || "",
          crop_stage: previousDayData.crop_stage ?? "",
          soil_moisture: previousDayData.soil_moisture ?? "",
          soil_temperature: previousDayData.soil_temperature ?? "",
          electrical_conductivity: previousDayData.electrical_conductivity ?? "",
          soil_ph: previousDayData.soil_ph ?? "",
          etc: previousDayData.etc ?? "",
          rainfall: previousDayData.rainfall ?? "",
          min_air_temp: previousDayData.min_air_temp ?? "",
          air_temperature: previousDayData.air_temperature ?? "",
          max_air_temp: previousDayData.max_air_temp ?? "",
          relative_humidity: previousDayData.relative_humidity ?? "",
        });
      } else {
        resetDailyForm();
      }
    }

    setDailyDialogOpen(true);
  };

  const handleCloseDailyDialog = () => {
    setDailyDialogOpen(false);
    setDailyFormError("");
  };

  const handleDailyFormChange = (field, value) => {
    setDailyForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const goToMyFields = () => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("user") || "null");
      const handle = storedUser?.username || storedUser?.email;
      navigate(handle ? `/u/${encodeURIComponent(handle)}` : "/profile");
    } catch {
      navigate("/profile");
    }
  };

  const openImportDialog = () => {
    if (!isViewingOwnFields) return;
    setImportFile(null);
    setImportPreview(null);
    setImportPolicy("skip_existing");
    setImportError("");
    setImportResult(null);
    setImportDialogOpen(true);
  };

  const closeImportDialog = () => {
    if (importLoading) return;
    setImportDialogOpen(false);
  };

  const handleImportFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setImportFile(file);
    setImportPreview(null);
    setImportResult(null);
    setImportError("");
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      setImportError("Only .xlsx files are supported.");
    } else if (file.size > MAX_DAILY_IMPORT_BYTES) {
      setImportError("The Excel file must not exceed 2 MB.");
    }
  };

  const importFormData = () => {
    const formData = new FormData();
    formData.append("field", selectedFieldId);
    formData.append("file", importFile);
    return formData;
  };

  const handlePreviewImport = async () => {
    if (!selectedFieldId || !importFile || importError) return;
    try {
      setImportLoading(true);
      setImportError("");
      setImportResult(null);
      const response = await api.post(
        "/daily-data/import/preview/",
        importFormData(),
      );
      setImportPreview(response.data);
    } catch (err) {
      setImportPreview(null);
      setImportError(err?.response?.data?.detail || "Could not validate the Excel file.");
    } finally {
      setImportLoading(false);
    }
  };

  const handleCommitImport = async () => {
    if (!importFile || !importPreview) return;
    try {
      setImportLoading(true);
      setImportError("");
      setImportResult(null);
      const formData = importFormData();
      formData.append("conflict_policy", importPolicy);
      const response = await api.post("/daily-data/import/", formData);
      setImportResult(response.data.result);
      await fetchDailyDataFromBackend();
      await fetchDailyHistoryFromBackend();
    } catch (err) {
      const responsePreview = err?.response?.data?.preview;
      if (responsePreview) setImportPreview(responsePreview);
      setImportError(err?.response?.data?.detail || "Could not import the Excel file.");
    } finally {
      setImportLoading(false);
    }
  };

  const handleDeleteDailyData = async () => {
    if (!isViewingOwnFields) return;
    if (!dailyDataToDelete) return;
    try {
      setDailyDeleteLoading(true);
      setDailyDataError("");
      await api.delete(`/daily-data/${dailyDataToDelete.id}/`);
      setDailyDataToDelete(null);
      await fetchDailyDataFromBackend();
      await fetchDailyHistoryFromBackend();
    } catch (err) {
      setDailyDataError(
        err?.response?.data?.detail || "Could not delete daily data."
      );
    } finally {
      setDailyDeleteLoading(false);
    }
  };

  const previousCropStage = useMemo(() => {
    const selectedDate = formatDate(calendarDate);
    if (!selectedDate || !selectedDateCycle) return "";
    return dailyHistory
      .filter(
        (item) =>
          item.date < selectedDate &&
          String(item.crop_cycle) === String(selectedDateCycle.id) &&
          item.crop_stage
      )
      .sort((a, b) => b.date.localeCompare(a.date))[0]?.crop_stage || "";
  }, [calendarDate, dailyHistory, selectedDateCycle]);

  const minimumCropStageIndex = Math.max(0, CROP_STAGES.indexOf(previousCropStage));
  const selectedImportPolicyPreview = importPreview?.policies?.[importPolicy] || null;

  const handleSaveDailyData = async () => {
    if (!isViewingOwnFields) return;
    if (!selectedDateCycle) {
      setDailyFormError("No crop cycle covers this date. Manage crop cycles in My Fields first.");
      return;
    }
    if (!dailyForm.crop_stage) {
      setDailyFormError("Please select a crop stage.");
      return;
    }

    const constraints = [
      ["Soil moisture", dailyForm.soil_moisture, 0, 100],
      ["Electrical conductivity", dailyForm.electrical_conductivity, 0, null],
      ["Soil pH", dailyForm.soil_ph, 0, 14],
      ["ETc", dailyForm.etc, 0, null],
      ["Rainfall", dailyForm.rainfall, 0, null],
      ["Humidity", dailyForm.relative_humidity, 0, 100],
    ];
    for (const [label, value, min, max] of constraints) {
      if (value === "") continue;
      const number = Number(value);
      if (!Number.isFinite(number) || number < min || (max !== null && number > max)) {
        setDailyFormError(
          max === null
            ? `${label} must be 0 or greater.`
            : `${label} must be between ${min} and ${max}.`
        );
        return;
      }
    }

    const temperatureValues = [
      ["Minimum air temperature", dailyForm.min_air_temp],
      ["Average air temperature", dailyForm.air_temperature],
      ["Maximum air temperature", dailyForm.max_air_temp],
    ];
    for (const [label, value] of temperatureValues) {
      if (value !== "" && !Number.isFinite(Number(value))) {
        setDailyFormError(`${label} must be a valid number.`);
        return;
      }
    }

    const minimumAirTemperature =
      dailyForm.min_air_temp === "" ? null : Number(dailyForm.min_air_temp);
    const averageAirTemperature =
      dailyForm.air_temperature === "" ? null : Number(dailyForm.air_temperature);
    const maximumAirTemperature =
      dailyForm.max_air_temp === "" ? null : Number(dailyForm.max_air_temp);

    if (
      minimumAirTemperature !== null &&
      maximumAirTemperature !== null &&
      minimumAirTemperature > maximumAirTemperature
    ) {
      setDailyFormError("Minimum air temperature cannot exceed maximum air temperature.");
      return;
    }
    if (
      averageAirTemperature !== null &&
      ((minimumAirTemperature !== null && averageAirTemperature < minimumAirTemperature) ||
        (maximumAirTemperature !== null && averageAirTemperature > maximumAirTemperature))
    ) {
      setDailyFormError(
        "Average air temperature must be between the minimum and maximum values."
      );
      return;
    }

    try {
      setDailyFormLoading(true);
      setDailyFormError("");

      const payload = {
        field: selectedFieldId,
        date: formatDate(calendarDate),
        crop_stage: dailyForm.crop_stage || null,
        soil_moisture:
          dailyForm.soil_moisture === ""
            ? null
            : Number(dailyForm.soil_moisture),
        soil_temperature:
          dailyForm.soil_temperature === ""
            ? null
            : Number(dailyForm.soil_temperature),
        electrical_conductivity:
          dailyForm.electrical_conductivity === ""
            ? null
            : Number(dailyForm.electrical_conductivity),
        soil_ph:
          dailyForm.soil_ph === ""
            ? null
            : Number(dailyForm.soil_ph),
        etc: dailyForm.etc === "" 
            ? null 
            : Number(dailyForm.etc),
        rainfall:
          dailyForm.rainfall === "" ? null : Number(dailyForm.rainfall),
        min_air_temp: minimumAirTemperature,
        air_temperature:
          averageAirTemperature,
        max_air_temp: maximumAirTemperature,
        relative_humidity:
          dailyForm.relative_humidity === ""
            ? null
            : Number(dailyForm.relative_humidity),
      };

      const existingRecord = dailyData.length > 0 ? dailyData[0] : null;

      if (existingRecord) {
        await api.put(`/daily-data/${existingRecord.id}/`, payload);
      } else {
        await api.post("/daily-data/create/", payload);
      }

      await fetchDailyDataFromBackend();
      await fetchDailyHistoryFromBackend();
      setDailyDialogOpen(false);
    } catch (err) {
      console.error(err);
      const errorData = err?.response?.data;
      const message = errorData
        ? Object.values(errorData).flat().join(" ")
        : "Could not save daily data.";
      setDailyFormError(message);
    } finally {
      setDailyFormLoading(false);
    }
  };

  const buildRecommenderPayload = () => {
  const item = dailyData?.[0] || null;

  if (!item) return null;

  return {
    field: selectedFieldId,
    date: formatDate(calendarDate),

    crop: item.crop || null,
    crop_stage: item.crop_stage || null,
    soil_moisture: item.soil_moisture,
    rainfall: item.rainfall,
    air_temperature: item.air_temperature,
    soil_ph: item.soil_ph,
    etc: item.etc,
  };
};

const handleRunRecommender = async () => {
  try {
    setRecommenderLoading(true);
    setRecommenderError("");
    setRecommenderResult(null);

    const payload = buildRecommenderPayload();

    if (!payload || !payload.crop) {
      setRecommenderError("No daily data available for recommender.");
      return;
    }

    const response = await api.post("/recommender/run/", payload);
    setRecommenderResult(response.data);
    if (response.data?.saved_to_record_id) {
      await fetchDailyDataFromBackend();
    }
  } catch (err) {
    console.error(err);
    const errorData = err?.response?.data;
    setRecommenderError(
      errorData
        ? Object.values(errorData).flat().join(" ")
        : "Could not run recommender."
    );
  } finally {
    setRecommenderLoading(false);
  }
};

const severityFromAction = (action) => {
  switch (action) {
    case "URGENT":
    case "HOLD":
      return "warning";
    case "WAIT":
      return "info";
    case "PLANNED":
      return "info";
    case "NO NEED":
    case "PROCEED":
      return "success";
    default:
      return "info";
  }
};

const recommenderAlerts = useMemo(() => {
  if (!recommenderResult?.result) return [];

  const fertilization = recommenderResult.result.fertilization;
  const irrigation = recommenderResult.result.irrigation;

  const alerts = [];

  if (fertilization?.level_1_action) {
    alerts.push({
      severity: severityFromAction(fertilization.level_1_action),
      title: `Fertilization: ${fertilization.level_1_action}`,
      date: new Date().toLocaleDateString("en-GB"),
      message: fertilization.then_text || "Fertilization recommendation generated.",
    });
  }

  if (irrigation?.level_1_action) {
    alerts.push({
      severity: severityFromAction(irrigation.level_1_action),
      title: `Irrigation: ${irrigation.level_1_action}`,
      date: new Date().toLocaleDateString("en-GB"),
      message: irrigation.then_text || "Irrigation recommendation generated.",
    });
  }

  return alerts;
}, [recommenderResult]);

const recommenderBestPractices = useMemo(() => {
  if (!recommenderResult?.result) return [];

  const fertilization = recommenderResult.result.fertilization;
  const irrigation = recommenderResult.result.irrigation;

  const practices = [];

  if (fertilization?.level_2_action) {
    practices.push(
      `Fertilizer recommendation: ${fertilization.level_2_action}.`
    );
  }

  if (fertilization?.then_text) {
    practices.push(`Fertilization guidance: ${fertilization.then_text}.`);
  }

  if (
    fertilization?.mode === "fallback" &&
    fertilization?.missing_inputs?.length
  ) {
    practices.push(
      `Fertilization ran in fallback mode due to missing: ${fertilization.missing_inputs.join(", ")}.`
    );
  }

  if (irrigation?.then_text) {
    practices.push(`Irrigation guidance: ${irrigation.then_text}.`);
  }

  if (irrigation?.mode === "fallback" && irrigation?.missing_inputs?.length) {
    practices.push(
      `Irrigation ran in fallback mode due to missing: ${irrigation.missing_inputs.join(", ")}.`
    );
  }

  return practices;
}, [recommenderResult]);

  // ----------------------------------------------------------------------
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          width: "100%",
          maxWidth: 1248,
          mx: "auto",
          px: { xs: 1, sm: 2 },
          py: { xs: 2, md: 3 },
        }}
      >
        <PageIntro title="Recommender" sx={{ mb: 3 }}>
          Get intelligent recommendations for experimental field selection based
          on location, weather data, soil analysis, and NDVI imagery
        </PageIntro>

        {/* GLOBAL SELECTION BAR */}
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid size={12}>
            <Card
              sx={{
                ...professionalCardSx,
                p: { xs: 2, md: 2.5 },
                minHeight: { md: 164 },
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                Field Selection
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Select {canSelectOwner ? "a user, " : ""}country and field. This
                configuration updates all charts,
                weather data, soil analysis, agronomy statistics, alerts and NDVI
                imagery across the recommender.
              </Typography>

              {fieldsError ? (
                <Alert severity="error" sx={{ mb: 2 }}>{fieldsError}</Alert>
              ) : !fieldsLoading && !fieldCountries.length ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {selectedOwner?.is_current_user
                    ? "You have not added any fields yet. Add a field from your profile first."
                    : "This user has no fields available to you."}
                </Alert>
              ) : null}
              {selectedOwner && !isViewingOwnFields ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Viewing {selectedOwner.display_name}'s fields in read-only mode.
                  You can inspect data and run the recommender, but you cannot edit
                  the field, crop cycles or daily data.
                </Alert>
              ) : null}


              <Grid container spacing={1.5} sx={{ alignItems: "flex-end" }}>
                {canSelectOwner ? (
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography
                      variant="overline"
                      sx={{
                        display: "block",
                        fontSize: "0.65rem",
                        lineHeight: 1.4,
                        mb: 0.5,
                      }}
                    >
                      Select User
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={selectedOwnerId}
                      onChange={(event) => {
                        setSelectedCountry("");
                        setSelectedFieldId("");
                        setSelectedOwnerId(String(event.target.value));
                      }}
                      disabled={fieldsLoading || !fieldOwners.length}
                      SelectProps={{
                        MenuProps: fieldSelectMenuProps,
                        IconComponent: KeyboardArrowDownRoundedIcon,
                      }}
                      sx={yellowSelectFieldSx}
                    >
                      {fieldOwners.map((owner) => (
                        <MenuItem key={owner.id} value={String(owner.id)}>
                          {getFieldOwnerLabel(owner)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                ) : null}

                <Grid size={{ xs: 12, sm: 4, md: canSelectOwner ? 3 : 4 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      display: "block",
                      fontSize: "0.65rem",
                      lineHeight: 1.4,
                      mb: 0.5,
                    }}
                  >
                    Select Country
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    disabled={fieldsLoading || !fieldCountries.length}
                    SelectProps={{
                      MenuProps: fieldSelectMenuProps,
                      IconComponent: KeyboardArrowDownRoundedIcon,
                    }}
                    sx={yellowSelectFieldSx}
                  >
                    {fieldCountries.map((country) => (
                      <MenuItem key={country} value={country}>
                        {country}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 8, md: canSelectOwner ? 5 : 8 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      display: "block",
                      fontSize: "0.65rem",
                      lineHeight: 1.4,
                      mb: 0.5,
                    }}
                  >
                    Select Field
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={selectedFieldId}
                    onChange={handleFieldChange}
                    disabled={fieldsLoading || !availableFields.length}
                    SelectProps={{
                      MenuProps: fieldSelectMenuProps,
                      IconComponent: KeyboardArrowDownRoundedIcon,
                    }}
                    sx={yellowSelectFieldSx}
                  >
                    {availableFields.map((field) => (
                      <MenuItem key={field.id} value={field.id}>
                        {field.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>

        {/* DETAILS / AGGREGATED DATA */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              variant="outlined"
              sx={{
                ...professionalCardSx,
                p: 2.5,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CardHeader
                title="Field Information"
                titleTypographyProps={{ fontSize: "1.1rem", fontWeight: 700 }}
                sx={{ p: 0, pb: 1, alignItems: "center" }}
              />
              <Divider sx={{ mb: 1.5 }} />

              <Stack
                spacing={1.05}
                sx={{
                  flex: 1,
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                <FieldInfoRow
                  label="Location"
                  value={currentFieldInfo.name}
                />
                <FieldInfoRow
                  label="City / Region"
                  value={currentFieldInfo.city}
                />
                <FieldInfoRow
                  label="Crop"
                  value={capitalizeFirst(currentFieldInfo.crop) || "N/A"}
                />
                <FieldInfoRow
                  label="Variety"
                  value={currentFieldInfo.variety}
                />
                <FieldInfoRow
                  label="Latitude"
                  value={
                    typeof currentFieldInfo.south === "number"
                      ? `${currentFieldInfo.south.toFixed(4)} N`
                      : "N/A"
                  }
                />
                <FieldInfoRow
                  label="Longitude"
                  value={
                    typeof currentFieldInfo.west === "number"
                      ? `${currentFieldInfo.west.toFixed(4)} E`
                      : "N/A"
                    }
                />
                <FieldInfoRow
                  label="Acres"
                  value={currentFieldInfo.acres}
                />
              </Stack>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              variant="outlined"
              sx={{
                ...professionalCardSx,
                p: 2.5,
                minHeight: { md: 425 },
              }}
            >
              <CardHeader
                title="Field Aggregated Data"
                titleTypographyProps={{ fontSize: "1.1rem", fontWeight: 700 }}
                action={
                  <Tabs
                    value={currentTab}
                    onChange={(e, v) => setCurrentTab(v)}
                    TabIndicatorProps={{ sx: { display: "none" } }}
                    sx={{
                      minHeight: 32,
                      "& .MuiTab-root": {
                        minHeight: 32,
                        minWidth: 0,
                        px: 1.1,
                        py: 0.5,
                        ml: 0.5,
                        borderRadius: 1.5,
                        fontSize: "0.72rem",
                        textTransform: "none",
                      },
                      "& .Mui-selected": {
                        bgcolor: "#ffbf00",
                        color: "#fff !important",
                      },
                    }}
                  >
                    <Tab label="On-Soil Data" />
                    <Tab label="Off-Soil Data" />
                  </Tabs>
                }
                sx={{
                  p: 0,
                  pb: 1,
                  alignItems: "flex-start",
                  "& .MuiCardHeader-action": { m: 0, alignSelf: "flex-start" },
                }}
              />
              <Divider />

              {/* OFF-SOIL TAB */}
              {currentTab === 1 && (
                <CardContent sx={{ p: 0, pt: 1, "&:last-child": { pb: 0 } }}>
                  {dailyHistoryLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 195,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : dailyHistoryError ? (
                    <Box
                      sx={{
                        minHeight: 195,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography color="error">{dailyHistoryError}</Typography>
                    </Box>
                  ) : weatherHistoryData.length > 0 ? (
                    <LazySyncedMetricCharts
                      data={weatherHistoryData}
                      groupId="plant-off-soil"
                      variant="offSoil"
                    />
                  ) : (
                    <Box
                      sx={{
                        minHeight: 195,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography color="text.secondary">
                        No off-soil measurements are available for this field yet.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              )}

              {/* ON-SOIL TAB */}
              {currentTab === 0 && (
                <CardContent sx={{ p: 0, pt: 1, "&:last-child": { pb: 0 } }}>
                  {dailyHistoryLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 195,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : dailyHistoryError ? (
                    <Box
                      sx={{
                        minHeight: 195,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography color="error">{dailyHistoryError}</Typography>
                    </Box>
                  ) : soilHistoryData.length > 0 ? (
                    <LazySyncedMetricCharts
                      data={soilHistoryData}
                      groupId="plant-on-soil"
                      variant="onSoil"
                    />
                  ) : (
                    <Box
                      sx={{
                        minHeight: 195,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography color="text.secondary">
                        No on-soil measurements are available for this field yet.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              )}
            </Card>
          </Grid>
        </Grid>

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{mt: 5, mb: 2.5, px: { xs: 0.5, sm: 1 } }}
        >
          <Divider sx={{ flex: 1, borderColor: "#d8dee8" }} />
          <Typography
            variant="overline"
            sx={{
              color: "text.secondary",
              fontSize: "1rem",
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: "0.16em",
            }}
          >
            Recommender
          </Typography>
          <Divider sx={{ flex: 1, borderColor: "#d8dee8" }} />
        </Stack>

        {/* DATE / DAILY / ALERTS / BEST PRACTICES */}
        <Grid container spacing={3} sx={{ mb: 3, alignItems: "stretch" }}>
          {/* Date Selection */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              variant="outlined"
              sx={{
                ...professionalCardSx,
                p: 2.5,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CardHeader
                title="Date Selection"
                titleTypographyProps={{ fontSize: "0.9rem", fontWeight: 700 }}
                sx={{ p: 0, pb: 1 }}
                action={
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setCalendarDate(new Date())}
                    sx={{
                      mt: -0.5,
                      minWidth: "auto",
                      px: 1.25,
                      "&:hover": {
                        bgcolor: "#fff7d6",
                        borderColor: "#f7b900",
                      },
                    }}
                  >
                    Today
                  </Button>
                }
              />
              <Divider />
              <CardContent
                sx={{
                  px: 0,
                  pt: 1,
                  pb: "0 !important",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <DateCalendar
                  value={calendarDate}
                  onChange={setCalendarDate}
                  slots={{ day: DailyDataDay }}
                  slotProps={{ day: { dataStages: dailyDataStages } }}
                  sx={{
                    width: "100%",
                    maxWidth: 320,
                    minHeight: 336,
                    maxHeight: "none",
                    mx: "auto",
                    "& .MuiPickersCalendarHeader-root": {
                      px: 1,
                      mt: 0,
                      mb: 1,
                      "& .MuiPickersCalendarHeader-labelContainer": {
                        overflow: "visible",
                        fontSize: "0.9rem",
                      },
                      "& .MuiPickersCalendarHeader-switchViewButton": {
                        color: "#9a7000",
                        "&:hover, &:focus, &:focus-visible": {
                          bgcolor: "#fff7d6",
                          color: "#7a5900",
                        },
                      },
                    },
                    "& .MuiPickersArrowSwitcher-root": {
                      gap: 0.75,
                      "& .MuiIconButton-root": {
                        padding: "6px",
                      },
                    },
                    "& .MuiDayCalendar-weekContainer": {
                      justifyContent: "space-around",
                    },
                    "& .MuiDayCalendar-header": {
                      justifyContent: "space-around",
                    },
                    "& .MuiPickersDay-root.Mui-selected": {
                      bgcolor: "#f7b900",
                      color: "#312600",
                      "&:hover": { bgcolor: "#dca600" },
                      "&:focus": { bgcolor: "#f7b900" },
                    },
                    "& .MuiPickersDay-root:not(.Mui-selected):hover": {
                      bgcolor: "#fff7d6",
                    },
                    "& .MuiPickersDay-today": { borderColor: "#f7b900" },
                    "& .MuiPickersDay-root:focus, & .MuiPickersDay-root:focus-visible, & .MuiPickersDay-root.Mui-focusVisible": {
                      outline: "none !important",
                      outlineOffset: "0 !important",
                      boxShadow: "none !important",
                    },
                    "& .MuiPickersYear-yearButton.Mui-selected": {
                      bgcolor: "#f7b900 !important",
                      color: "#312600 !important",
                      "&:hover": { bgcolor: "#dca600 !important" },
                      "&:focus, &:focus-visible": { bgcolor: "#f7b900 !important" },
                    },
                    "& .MuiPickersYear-yearButton:hover": {
                      bgcolor: "#fff7d6",
                    },
                    "& .MuiYearCalendar-button:hover": {
                      bgcolor: "#fff7d6",
                    },
                    "& .MuiPickersYear-yearButton:focus, & .MuiPickersYear-yearButton:focus-visible, & .MuiPickersYear-yearButton.Mui-focusVisible, & .MuiYearCalendar-button:focus, & .MuiYearCalendar-button:focus-visible": {
                      outline: "none !important",
                      outlineOffset: "0 !important",
                      boxShadow: "none !important",
                    },
                    "& .MuiYearCalendar-root": {
                      width: "100%",
                      maxWidth: "100%",
                    },
                    "& .MuiYearCalendar-button": {
                      width: "100%",
                    },
                    "& .MuiYearCalendar-button.Mui-selected": {
                      bgcolor: "#f7b900 !important",
                      color: "#312600 !important",
                      "&:hover": { bgcolor: "#dca600 !important" },
                    },
                  }}
                />
                <Box sx={{ width: "100%", maxWidth: 320, mx: "auto", mt: 1.5, pt: 1.5, borderTop: "1px solid #e5e7eb" }}>
                  <Typography variant="overline" sx={{ display: "block", mb: 0.75, color: "text.secondary", fontWeight: 800, lineHeight: 1.4 }}>
                    Crop stage legend
                  </Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", columnGap: 1.5, rowGap: 0.85 }}>
                    {CROP_STAGES.map((stage) => (
                      <Stack key={stage} direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: CROP_STAGE_COLORS[stage], flex: "0 0 auto" }} />
                        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", lineHeight: 1.3 }}>
                          {stage}
                        </Typography>
                      </Stack>
                    ))}
                  </Box>
                  <Typography sx={{ mt: 1.25, color: "text.secondary", fontSize: "0.7rem", fontStyle: "italic", lineHeight: 1.35 }}>
                    *Crop stages are presented using a simplified Zadoks growth-stage framework.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Daily Details */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              variant="outlined"
              sx={{
                ...professionalCardSx,
                p: 2.5,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CardHeader
                title={`Daily Data for ${calendarDate.toLocaleDateString(
                  "en-GB"
                )}`}
                subheader={
                  selectedDateCycle
                    ? `${selectedDateCycle.name} · ${selectedDateCycle.status_display}`
                    : activeCycle
                      ? `Active cycle: ${activeCycle.name}`
                      : "No crop cycle"
                }
                titleTypographyProps={{ fontWeight: 700, fontSize: "0.8rem" }}
                subheaderTypographyProps={{ fontSize: "0.72rem", color: "text.secondary" }}
                action={
                  dailyData.length > 0 && isViewingOwnFields ? (
                    <Stack direction="row" spacing={0.25}>
                      <Tooltip title="Edit daily data">
                        <IconButton
                          size="small"
                          aria-label="Edit daily data"
                          onClick={handleOpenDailyDialog}
                          disabled={!selectedDateCycle || selectedCycleIsCompleted}
                          sx={{
                            color: "#4b5563",
                            "&:hover": { color: "#111", bgcolor: "#fff7d6" },
                          }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete daily data">
                        <IconButton
                          size="small"
                          aria-label="Delete daily data"
                          onClick={() => setDailyDataToDelete(dailyData[0])}
                          disabled={selectedCycleIsCompleted}
                          sx={{
                            color: "#6b7280",
                            "&:hover": { color: "#dc2626", bgcolor: "#fff1f2" },
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : null
                }
                sx={{ alignItems: "flex-start" }}
              />
              <Divider />

              <CardContent sx={{ p: 0, pt: 0, "&:last-child": { pb: 0 } }}>
                {isViewingOwnFields && selectedDateCycle && !selectedCycleIsCompleted ? (
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    startIcon={<UploadFileOutlinedIcon />}
                    onClick={openImportDialog}
                    disabled={!selectedFieldId || cropCyclesLoading}
                    sx={{
                      mb: 1.5,
                      textTransform: "none",
                      borderColor: "#f7b900",
                      color: "#7a5900",
                      "&:hover": { borderColor: "#dca600", bgcolor: "#fff7d6" },
                    }}
                  >
                    Import Excel
                  </Button>
                ) : null}
                {!cropCyclesLoading && selectedFieldId && !selectedDateCycle ? (
                  <Alert
                    severity="warning"
                    sx={{ mb: 1.5 }}
                    action={
                      isViewingOwnFields ? (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={goToMyFields}
                          sx={{ ...yellowActionButtonSx, minWidth: "auto", px: 1.5 }}
                        >
                          My Fields
                        </Button>
                      ) : null
                    }
                  >
                    No crop cycle covers this date. Manage the field's crop cycles before adding data.
                  </Alert>
                ) : null}
                {selectedCycleIsCompleted ? (
                  <Alert
                    severity="info"
                    sx={{
                      mb: 1.5,
                      py: 0.5,
                      px: 1,
                      "& .MuiAlert-icon": { mr: 0.75, py: 0.25 },
                      "& .MuiAlert-message": { py: 0.25, fontSize: "0.76rem", lineHeight: 1.4 },
                    }}
                  >
                    This cycle and its daily data are read-only; you cannot add, edit, or delete data.
                  </Alert>
                ) : null}
                {dailyDataLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : dailyDataError ? (
                  <Typography color="error" sx={{ py: 2, textAlign: "center" }}>
                    {dailyDataError}
                  </Typography>
                ) : dailyData.length > 0 ? (
                  <Stack spacing={2}>
                    {dailyData.map((item) => (
                      <Box key={item.id}>
                        <Box>
                          <Typography
                            variant="overline"
                            sx={metricHeadingSx}
                          >
                            Crop Information
                          </Typography>

                          <Box sx={{ mt: 0.5 }}>
                            <DataRow
                              label="Crop"
                              value={capitalizeFirst(item.crop) || "No crop"}
                            />
                            <DataRow
                              label="Crop Stage"
                              value={item.crop_stage || "No stage"}
                            />
                            <DataRow
                              label="Crop ETc (mm/day)"
                              value={item.etc != null ? `${item.etc}` : "-"}
                            />
                          </Box>
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        <Box>
                          <Typography variant="overline" sx={metricHeadingSx}>
                            On-Soil Data
                          </Typography>

                          <Box sx={{ mt: 0.5 }}>
                            <DataRow
                              label="Soil Moisture (%)"
                              value={
                                item.soil_moisture != null
                                  ? `${item.soil_moisture}`
                                  : "-"
                              }
                            />
                            <DataRow
                              label="Soil Temp. (°C)"
                              value={
                                item.soil_temperature != null
                                  ? `${item.soil_temperature}`
                                  : "-"
                              }
                            />
                            <DataRow
                              label="EC (dS/m)"
                              value={
                                item.electrical_conductivity != null
                                  ? `${item.electrical_conductivity}`
                                  : "-"
                              }
                            />
                            <DataRow
                              label="Soil pH"
                              value={item.soil_ph != null ? `${item.soil_ph}` : "-"}
                            />
                          </Box>
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        <Box>
                          <Typography
                            variant="overline"
                            sx={metricHeadingSx}
                          >
                            Off-Soil Data
                          </Typography>

                          <Box sx={{ mt: 0.5 }}>
                            <DataRow
                              label="Daily Rainfall (mm)"
                              value={
                                item.rainfall != null ? `${item.rainfall}` : "-"
                              }
                            />
                            <DataRow
                              label="Min Air Temp (°C)"
                              value={
                                item.min_air_temp != null
                                  ? `${item.min_air_temp}`
                                  : "-"
                              }
                            />
                            <DataRow
                              label="Avg Air Temp (°C)"
                              value={
                                item.air_temperature != null
                                  ? `${item.air_temperature}`
                                  : "-"
                              }
                            />
                            <DataRow
                              label="Max Air Temp (°C)"
                              value={
                                item.max_air_temp != null
                                  ? `${item.max_air_temp}`
                                  : "-"
                              }
                            />
                            <DataRow
                              label="Humidity (%)"
                              value={
                                item.relative_humidity != null
                                  ? `${item.relative_humidity}`
                                  : "-"
                              }
                            />
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Box
                    sx={{
                      minHeight: 300,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    <Typography color="text.secondary" sx={{ mb: 1.5 }}>
                      No daily data for this date.
                    </Typography>

                    {isViewingOwnFields && !selectedCycleIsCompleted ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleOpenDailyDialog}
                        disabled={!selectedDateCycle || cropCyclesLoading}
                      >
                        Add Data
                      </Button>
                    ) : null}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Alerts */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              variant="outlined"
              sx={{
                ...professionalCardSx,
                p: 2.5,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CardHeader
                title="Alerts"
                titleTypographyProps={{ fontSize: "0.9rem", fontWeight: 700 }}
                sx={{ p: 0, pb: 1 }}
                action={
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleRunRecommender}
                    disabled={
                      recommenderLoading ||
                      dailyDataLoading ||
                      dailyData.length === 0 ||
                      !dailyData[0]?.crop_cycle
                    }
                    sx={{ mt: -0.5, minWidth: "auto", px: 1.25 }}
                  >
                    {recommenderLoading ? "Running..." : "Check Alerts"}
                  </Button>
                }
              />
              <Divider />
              <CardContent sx={{ p: 0, pt: 1.5, "&:last-child": { pb: 0 } }}>
                  {recommenderError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {recommenderError}
                    </Alert>
                  )}

                  <Stack spacing={2} sx={{ mt: 1 }}>
                    {recommenderAlerts.length > 0 ? (
                      recommenderAlerts.map((alert, i) => (
                        <Alert
                          key={i}
                          severity={alert.severity}
                          sx={{ alignItems: "center" }}
                        >
                          <Box sx={{ flexGrow: 1 }}>
                            <AlertTitle>{alert.title}</AlertTitle>
                            {alert.message}
                          </Box>
                          <Typography variant="caption" sx={{ ml: 2 }}>
                            {alert.date}
                          </Typography>
                        </Alert>
                      ))
                    ) : (
                      <Typography color="text.secondary">
                        No alerts at this time.
                      </Typography>
                    )}
                  </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Best Practices */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              variant="outlined"
              sx={{
                ...professionalCardSx,
                p: 2.5,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                bgcolor: "#f2fced",
                borderColor: "#a7efbd",
              }}
            >
              <CardHeader
                title="Best Practices"
                titleTypographyProps={{ fontSize: "0.9rem", fontWeight: 700 }}
                sx={{ p: 0, pb: 1 }}
              />
              <Divider />
              <CardContent sx={{ p: 0, pt: 1, "&:last-child": { pb: 0 } }}>
                <List dense>
                  {recommenderBestPractices.length > 0 ? (
                    recommenderBestPractices.map((text, i) => (
                      <ListItem key={i}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleOutlineIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={text} />
                      </ListItem>
                    ))
                  ) : (
                    <Typography color="text.secondary">
                      No best practices available.
                    </Typography>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* NDVI */}
        <Stack spacing={3} sx={{ mb: 4 }}>
          <Box>
            <Card
              variant="outlined"
              sx={{
                ...professionalCardSx,
                p: 2.5,
                minHeight: { md: 305 },
              }}
            >
              <CardHeader
                title="NDVI/RGB Data Generation Tool"
                titleTypographyProps={{ fontSize: "1.1rem", fontWeight: 700 }}
                sx={{ p: 0, pb: 1 }}
              />
              <Divider />
              <CardContent sx={{ p: 0, pt: 1, "&:last-child": { pb: 0 } }}>
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    Generate the latest available low-cloud NDVI/RGB image for{" "}
                    <Box component="span" sx={{ color: "#f0a800" }}>
                      {currentFieldInfo.name}
                    </Box>
                  </Typography>

                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ mb: 0.5, color: "text.secondary" }}
                    >
                      Date of Imagery:{" "}
                      <Box component="span" sx={{ color: "#f0a800" }}>
                        {formatDisplayDate(calendarDate)}
                      </Box>
                    </Typography>
                    {/* <TextField
                      value={calendarDate
                        .toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                        .replace(/ /g, "-")}
                      size="small"
                      InputProps={{ readOnly: true }}
                      sx={{
                        width: 150,
                        "& .MuiInputBase-root": { height: 38 },
                      }}
                    /> */}
                  </Box>

                  <Grid container spacing={1.5}>
                    {["west", "south", "east", "north"].map((key) => (
                      <Grid size={{ xs: 12, sm: 6, md: 3 }} key={key}>
                        <Typography
                          variant="overline"
                          sx={{
                            display: "block",
                            fontSize: "0.65rem",
                            lineHeight: 1.4,
                            mb: 0.5,
                          }}
                        >
                          {key}
                        </Typography>
                        <TextField
                          value={coords?.[key] ?? ""}
                          fullWidth
                          size="small"
                          disabled
                          helperText={`From '${currentFieldInfo.name}'`}
                          sx={disabledCoordinateFieldSx}
                        />
                      </Grid>
                    ))}
                  </Grid>

                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="center"
                    flexWrap="wrap"
                  >
                    <Button
                      variant="contained"
                      onClick={handleSubmitNDVI}
                      disabled={loading || !isCoordsValid}
                      sx={yellowActionButtonSx}
                      startIcon={
                        loading ? <CircularProgress size={18} /> : null
                      }
                    >
                      {loading ? "Generating…" : " Generate Image"}
                    </Button>

                    {!isCoordsValid && (
                      <Typography variant="body2" color="text.secondary">
                        Invalid or missing coordinates.
                      </Typography>
                    )}
                  </Stack>

                  {loading && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Please stay on this page while your image is being generated. Leaving or refreshing the page before it completes will cancel the generation. This may take a few moments.
                    </Alert>
                  )}

                  {error && <Alert severity="error">{error}</Alert>}

                  {images && (
                    <Box sx={{ pt: 1 }}>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          Latest available Sentinel-2 image
                        </Typography>
                        <Typography variant="body2">
                          Using the latest available acquisition from <strong>{formatDisplayDate(images.source_date)}</strong>.
                        </Typography>
                      </Alert>

                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, lg: 6 }}>
                          <Typography variant="subtitle1">NDVI</Typography>
                          <CardMedia
                            component="img"
                            image={`${API_BASE.replace(/\/api\/?$/, "")}${images.ndvi_png}`}
                            onClick={() =>
                              openImageModal(
                                `${API_BASE.replace(/\/api\/?$/, "")}${images.ndvi_png}`
                              )
                            }
                            sx={{
                              width: "100%",
                              height: "auto",
                              maxHeight: 260,
                              cursor: "pointer",
                              objectFit: "contain",
                              borderRadius: 1,
                              border: "1px solid #ddd",
                            }}
                          />

                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems="center"
                            justifyContent="center"
                            sx={{ mt: 1.5 }}
                          >
                            <Button
                              component="a"
                              href={`${API_BASE.replace(/\/api\/?$/, "")}${images.ndvi_png}?download=1`}
                              download
                              variant="contained"
                              size="small"
                              sx={yellowActionButtonSx}
                            >
                              Download NDVI PNG
                            </Button>
                            <Button
                              component="a"
                              href={`${API_BASE.replace(/\/api\/?$/, "")}${images.ndvi_tiff}?download=1`}
                              download
                              variant="contained"
                              size="small"
                              sx={yellowActionButtonSx}
                            >
                              Download NDVI TIFF
                            </Button>
                          </Stack>
                        </Grid>

                        <Grid size={{ xs: 12, lg: 6 }}>
                          <Typography variant="subtitle1">RGB</Typography>
                          <CardMedia
                            component="img"
                            image={`${API_BASE.replace(/\/api\/?$/, "")}${images.rgb_png}`}
                            onClick={() =>
                              openImageModal(
                                `${API_BASE.replace(/\/api\/?$/, "")}${images.rgb_png}`
                              )
                            }
                            sx={{
                              width: "100%",
                              height: "auto",
                              maxHeight: 260,
                              cursor: "pointer",
                              objectFit: "contain",
                              borderRadius: 1,
                              border: "1px solid #ddd",
                            }}
                          />

                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems="center"
                            justifyContent="center"
                            sx={{ mt: 1.5 }}
                          >
                            <Button
                              component="a"
                              href={`${API_BASE.replace(/\/api\/?$/, "")}${images.rgb_png}?download=1`}
                              download
                              variant="contained"
                              size="small"
                              sx={yellowActionButtonSx}
                            >
                              Download RGB PNG
                            </Button>
                            <Button
                              component="a"
                              href={`${API_BASE.replace(/\/api\/?$/, "")}${images.rgb_tiff}?download=1`}
                              download
                              variant="contained"
                              size="small"
                              sx={yellowActionButtonSx}
                            >
                              Download RGB TIFF
                            </Button>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ display: "none" }}>
            <Card
              variant="outlined"
              sx={{
                ...professionalCardSx,
                p: 2.5,
                minHeight: { md: 245 },
              }}
            >
              <CardHeader
                title="NDVI Results"
                titleTypographyProps={{ fontSize: "1.1rem", fontWeight: 700 }}
                sx={{ p: 0, pb: 1 }}
              />
              <Divider />

              <CardContent sx={{ p: 0, pt: 2, "&:last-child": { pb: 0 } }}>
                {loading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : images ? (
                  <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Latest available Sentinel-2 image · approximately 1 × 1 km around the station
                      </Typography>
                      <Typography variant="body2">
                        Using the latest available acquisition from <strong>{formatDisplayDate(images.source_date)}</strong>.
                      </Typography>
                    </Alert>

                    <Grid container spacing={2}>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <Typography variant="subtitle1">NDVI</Typography>

                      <CardMedia
                        component="img"
                        image={`${API_BASE.replace(/\/api\/?$/, "")}${images.ndvi_png}`}
                        onClick={() =>
                          openImageModal(
                            `${API_BASE.replace(/\/api\/?$/, "")}${images.ndvi_png}`
                          )
                        }
                        sx={{
                          width: "100%",
                          height: "auto",
                          maxHeight: 260,
                          cursor: "pointer",
                          objectFit: "contain",
                          borderRadius: 1,
                          border: "1px solid #ddd",
                          transition: "0.2s",
                          "&:hover": { transform: "scale(1.03)" },
                        }}
                      />

                      <Box sx={{ display: "none", mt: 1.5 }}>
                        <Box
                          sx={{
                            height: 12,
                            borderRadius: 1,
                            background:
                              "linear-gradient(90deg, #a50026 0%, #f46d43 25%, #fee08b 50%, #66bd63 75%, #006837 100%)",
                          }}
                        />
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          sx={{ mt: 0.5 }}
                        >
                          <Typography variant="caption">Low / bare soil</Typography>
                          <Typography variant="caption">Medium</Typography>
                          <Typography variant="caption">High vegetation</Typography>
                        </Stack>
                      </Box>

                      {false && images.ndvi_stats && (
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                          {[
                            ["Mean NDVI", images.ndvi_stats.mean],
                            ["NDVI range", `${images.ndvi_stats.min} – ${images.ndvi_stats.max}`],
                            ["Low (< 0.3)", `${images.ndvi_stats.low_percent}%`],
                            ["Medium (0.3–0.6)", `${images.ndvi_stats.medium_percent}%`],
                            ["High (≥ 0.6)", `${images.ndvi_stats.high_percent}%`],
                          ].map(([label, value]) => (
                            <Grid size={{ xs: 6 }} key={label}>
                              <Box
                                sx={{
                                  p: 1,
                                  borderRadius: 1,
                                  bgcolor: "#f7f7f5",
                                  border: "1px solid #e7e5e4",
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  {label}
                                </Typography>
                                <Typography fontWeight={700}>{value}</Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      )}

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        sx={{ mt: 1.5 }}
                      >
                        <Button
                          component="a"
                          href={`${API_BASE.replace(/\/api\/?$/, "")}${images.ndvi_png}?download=1`}
                          download
                          variant="contained"
                          size="small"
                        >
                          Download NDVI image (PNG)
                        </Button>
                        <Button
                          component="a"
                          href={`${API_BASE.replace(/\/api\/?$/, "")}${images.ndvi_tiff}?download=1`}
                          download
                          variant="outlined"
                          size="small"
                        >
                          GeoTIFF for GIS
                        </Button>
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 6 }}>
                      <Typography variant="subtitle1">RGB</Typography>

                      <CardMedia
                        component="img"
                        image={`${API_BASE.replace(/\/api\/?$/, "")}${images.rgb_png}`}
                        onClick={() =>
                          openImageModal(
                            `${API_BASE.replace(/\/api\/?$/, "")}${images.rgb_png}`
                          )
                        }
                        sx={{
                          width: "100%",
                          height: "auto",
                          maxHeight: 260,
                          cursor: "pointer",
                          objectFit: "contain",
                          borderRadius: 1,
                          border: "1px solid #ddd",
                          transition: "0.2s",
                          "&:hover": { transform: "scale(1.03)" },
                        }}
                      />

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        sx={{ mt: 1.5 }}
                      >
                        <Button
                          component="a"
                          href={`${API_BASE.replace(/\/api\/?$/, "")}${images.rgb_png}?download=1`}
                          download
                          variant="contained"
                          size="small"
                        >
                          Download RGB image (PNG)
                        </Button>
                        <Button
                          component="a"
                          href={`${API_BASE.replace(/\/api\/?$/, "")}${images.rgb_tiff}?download=1`}
                          download
                          variant="outlined"
                          size="small"
                        >
                          GeoTIFF for GIS
                        </Button>
                      </Stack>
                    </Grid>
                    </Grid>
                  </>
                ) : (
                  <Box
                    sx={{
                      minHeight: 160,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      bgcolor: "#f2fced",
                      border: "1px solid #a7efbd",
                      borderRadius: 2,
                      color: "text.secondary",
                    }}
                  >
                    <Typography>No NDVI data available.</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        </Stack>
      </Box>

      {/* NDVI image modal */}
      <Dialog open={isModalOpen} onClose={closeImageModal} maxWidth="lg" fullWidth>
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#fafafa",
          }}
        >
          <img
            src={modalImageSrc}
            alt="NDVI RGB modal"
            style={{
              maxWidth: "95%",
              maxHeight: "80vh",
              objectFit: "contain",
              borderRadius: "8px",
              boxShadow: "0 0 12px rgba(0,0,0,0.25)",
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Daily data add/edit dialog */}
      <Dialog
        open={dailyDialogOpen}
        onClose={handleCloseDailyDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dailyData.length > 0 ? "Edit Daily Data" : "Add Daily Data"}
        </DialogTitle>

        <DialogContent>
          {selectedDateCycle ? (
            <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
              Data for this date belongs to <strong>{selectedDateCycle.name}</strong>.
            </Alert>
          ) : null}
          <Grid
            container
            spacing={2}
            sx={{
              mt: 0.8,
              "& .MuiInputBase-input": {
                color: "#000",
              },
              "& input[type=number]": {
                MozAppearance: "textfield",
              },
              "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                WebkitAppearance: "none",
                margin: 0,
              },
              "& .MuiOutlinedInput-root": {
                bgcolor: "#fff",
                "& fieldset": { borderColor: "#f0a800" },
                "&:hover fieldset": { borderColor: "#f0a800" },
                "&.Mui-focused fieldset": { borderColor: "#f0a800" },
              },
              "& .MuiInputLabel-root": {
                color: "#000",
                "&.Mui-focused": { color: "#f0a800" },
                "&.MuiInputLabel-shrink": { color: "#f0a800" },
              },
            }}
          >
            <Grid size={12}>
              <Card variant="outlined" sx={dailyDataSectionCardSx}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                    <Box sx={dailyDataSectionIconSx}>
                      <SpaOutlinedIcon fontSize="small" />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={800}>
                      Crop
                    </Typography>
                  </Stack>
                  <Grid container spacing={2}>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Crop"
                fullWidth
                value={dailyForm.crop}
                disabled
                helperText="Crop is defined in My Fields."
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Crop Stage"
                fullWidth
                select
                value={dailyForm.crop_stage}
                SelectProps={{
                  IconComponent: KeyboardArrowDownRoundedIcon,
                  MenuProps: fieldSelectMenuProps,
                }}
                onChange={(e) =>
                  handleDailyFormChange("crop_stage", e.target.value)
                }
                helperText={
                  previousCropStage
                    ? `Stages before ${previousCropStage} are unavailable within ${selectedDateCycle?.name || "this crop cycle"}.`
                    : "Select the current development stage for this crop cycle."
                }
              >
                {CROP_STAGES.map((stage, index) => (
                  <MenuItem key={stage} value={stage} disabled={index < minimumCropStageIndex}>
                    {index + 1}. {stage}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Crop Evapotranspiration (ETc, mm/day)"
                fullWidth
                type="number"
                value={dailyForm.etc}
                inputProps={{ min: 0, step: "any" }}
                helperText="Allowed value: 0 or greater."
                onChange={(e) =>
                  handleDailyFormChange("etc", e.target.value)
                }
              />
            </Grid>

                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={12}>
              <Card variant="outlined" sx={dailyDataSectionCardSx}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                    <Box sx={dailyDataSectionIconSx}>
                      <SensorsOutlinedIcon fontSize="small" />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={800}>
                      On-Soil Data
                    </Typography>
                  </Stack>
                  <Grid container spacing={2}>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Soil Moisture (%)"
                fullWidth
                type="number"
                inputProps={{ min: 0, max: 100 }}
                helperText="Allowed range: 0–100%."
                value={dailyForm.soil_moisture}
                onChange={(e) =>
                  handleDailyFormChange("soil_moisture", e.target.value)
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Soil Temperature (°C)"
                fullWidth
                type="number"
                value={dailyForm.soil_temperature}
                inputProps={{ step: "any" }}
                helperText="Negative values are allowed."
                onChange={(e) =>
                  handleDailyFormChange("soil_temperature", e.target.value)
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Electrical Conductivity (dS/m)"
                fullWidth
                type="number"
                value={dailyForm.electrical_conductivity}
                inputProps={{ min: 0, step: "any" }}
                helperText="Allowed value: 0 or greater."
                onChange={(e) =>
                  handleDailyFormChange(
                    "electrical_conductivity",
                    e.target.value
                  )
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Soil pH"
                fullWidth
                type="number"
                value={dailyForm.soil_ph}
                inputProps={{ min: 0, max: 14, step: "any" }}
                helperText="Valid pH range: 0–14."
                onChange={(e) =>
                  handleDailyFormChange("soil_ph", e.target.value)
                }
              />
            </Grid>

                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={12}>
              <Card variant="outlined" sx={dailyDataSectionCardSx}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                    <Box sx={dailyDataSectionIconSx}>
                      <CloudOutlinedIcon fontSize="small" />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={800}>
                      Off-Soil Data
                    </Typography>
                  </Stack>
                  <Grid container spacing={2}>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Daily Rainfall (mm)"
                fullWidth
                type="number"
                value={dailyForm.rainfall}
                inputProps={{ min: 0, step: "any" }}
                helperText="Allowed value: 0 or greater."
                onChange={(e) =>
                  handleDailyFormChange("rainfall", e.target.value)
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Minimum Daily Air Temperature (°C)"
                fullWidth
                type="number"
                value={dailyForm.min_air_temp}
                inputProps={{ step: "any" }}
                helperText="Negative values are allowed."
                onChange={(e) =>
                  handleDailyFormChange("min_air_temp", e.target.value)
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Maximum Daily Air Temperature (°C)"
                fullWidth
                type="number"
                value={dailyForm.max_air_temp}
                inputProps={{ step: "any" }}
                helperText="Negative values are allowed."
                onChange={(e) =>
                  handleDailyFormChange("max_air_temp", e.target.value)
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Average Daily Air Temperature (°C)"
                fullWidth
                type="number"
                value={dailyForm.air_temperature}
                inputProps={{ step: "any" }}
                helperText="Negative values are allowed."
                onChange={(e) =>
                  handleDailyFormChange("air_temperature", e.target.value)
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Humidity (%)"
                fullWidth
                type="number"
                value={dailyForm.relative_humidity}
                inputProps={{ min: 0, max: 100 }}
                helperText="Allowed range: 0–100%."
                onChange={(e) =>
                  handleDailyFormChange("relative_humidity", e.target.value)
                }
              />
            </Grid>

                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {dailyFormError && (
              <Grid size={12}>
                <Alert severity="error">{dailyFormError}</Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{ px: 3, pt: 0, pb: 2, justifyContent: "flex-end" }}
        >
          <Button onClick={handleCloseDailyDialog} disabled={dailyFormLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveDailyData}
            disabled={dailyFormLoading || !selectedDateCycle || selectedCycleIsCompleted}
            sx={{ ...yellowActionButtonSx, minWidth: 96 }}
          >
            {dailyFormLoading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={importDialogOpen}
        onClose={closeImportDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import Daily Data from Excel</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              The measurements will be imported only into <strong>{currentFieldInfo?.name || "the selected field"}</strong>. Each date will be matched automatically to the crop cycle that covers it.
            </Alert>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileOutlinedIcon />}
                disabled={importLoading}
                sx={{ textTransform: "none", borderColor: "#f7b900", color: "#7a5900", "&:hover": { borderColor: "#dca600", bgcolor: "#fff7d6" } }}
              >
                Select Excel file
                <input
                  hidden
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleImportFileChange}
                />
              </Button>
              <Typography variant="body2" color={importFile ? "text.primary" : "text.secondary"} sx={{ overflowWrap: "anywhere" }}>
                {importFile ? importFile.name : "No file selected"}
              </Typography>
              <Button
                component="a"
                href={DAILY_DATA_TEMPLATE_URL}
                download
                size="small"
                startIcon={<DownloadOutlinedIcon />}
                sx={{ textTransform: "none", color: "#7a5900" }}
              >
                Download Template
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Accepted format: .xlsx. Maximum size: 2 MB. Maximum data rows: 1,000.
            </Typography>

            {importError && <Alert severity="error">{importError}</Alert>}
            {importResult && (
              <Alert severity="success">
                Import completed: {importResult.created} new, {importResult.updated} updated, {importResult.skipped} skipped.
              </Alert>
            )}

            {importPreview && !importResult && (
              <>
                <Divider />
                <Typography variant="subtitle1" fontWeight={700}>Validation preview</Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip label={`New rows: ${importPreview.new_rows}`} color="success" variant="outlined" />
                  <Chip label={`Existing rows: ${importPreview.existing_rows}`} color="warning" variant="outlined" />
                  <Chip label={`Invalid rows: ${selectedImportPolicyPreview?.invalid_count || 0}`} color={selectedImportPolicyPreview?.invalid_count ? "error" : "default"} variant="outlined" />
                  <Chip label={`Excel duplicates: ${importPreview.duplicate_count}`} color={importPreview.duplicate_count ? "error" : "default"} variant="outlined" />
                </Stack>

                {importPreview.duplicates?.length > 0 && (
                  <Alert severity="error">
                    <AlertTitle>Duplicate dates inside the Excel file</AlertTitle>
                    {importPreview.duplicates.map((duplicate) => (
                      <Typography key={`${duplicate.row}-${duplicate.date}`} variant="body2">
                        Row {duplicate.row}: {duplicate.date} already appears on row {duplicate.first_row}.
                      </Typography>
                    ))}
                  </Alert>
                )}

                {selectedImportPolicyPreview?.invalid_rows?.length > 0 && (
                  <Alert severity="error">
                    <AlertTitle>Rows that must be corrected</AlertTitle>
                    {selectedImportPolicyPreview.invalid_rows.map((invalid) => (
                      <Typography key={invalid.row} variant="body2" sx={{ mb: 0.5 }}>
                        Row {invalid.row}: {invalid.messages.join(" ")}
                      </Typography>
                    ))}
                  </Alert>
                )}

                <Box>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Existing dates
                  </Typography>
                  <RadioGroup
                    value={importPolicy}
                    onChange={(event) => setImportPolicy(event.target.value)}
                  >
                    <FormControlLabel
                      value="skip_existing"
                      control={<Radio sx={{ color: "#dca600", "&.Mui-checked": { color: "#f7b900" } }} />}
                      label="Skip existing (recommended)"
                    />
                    <FormControlLabel
                      value="update_existing"
                      control={<Radio sx={{ color: "#dca600", "&.Mui-checked": { color: "#f7b900" } }} />}
                      label="Update existing"
                    />
                  </RadioGroup>
                  <Typography variant="caption" color="text.secondary">
                    Update existing changes only cells that contain a value in Excel. Blank cells never erase stored measurements.
                  </Typography>
                </Box>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeImportDialog} disabled={importLoading}>
            {importResult ? "Close" : "Cancel"}
          </Button>
          {!importResult && !importPreview && (
            <Button
              variant="contained"
              onClick={handlePreviewImport}
              disabled={!importFile || Boolean(importError) || importLoading}
              sx={{ ...yellowActionButtonSx, minWidth: 130 }}
            >
              {importLoading ? "Uploading..." : "Upload file"}
            </Button>
          )}
          {!importResult && importPreview && (
            <Button
              variant="contained"
              onClick={handleCommitImport}
              disabled={!selectedImportPolicyPreview?.can_import || importLoading}
              sx={{ ...yellowActionButtonSx, minWidth: 130 }}
            >
              {importLoading ? "Importing..." : "Import data"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={Boolean(dailyDataToDelete)}
        title="Do you want to delete this daily data?"
        description={`This will permanently delete the data for ${calendarDate.toLocaleDateString("en-GB")}.`}
        loading={dailyDeleteLoading}
        onClose={() => setDailyDataToDelete(null)}
        onConfirm={handleDeleteDailyData}
      />
    </LocalizationProvider>
  );
}
